(() => {
  if (window.NavelisQuickView) return;

  const dialog = document.querySelector('[data-quick-view-dialog]');
  if (!dialog) return;

  const content = dialog.querySelector('[data-quick-view-content]');
  const status = dialog.querySelector('[data-quick-view-status]');
  let trigger = null;
  let requestController = null;
  let priceController = null;
  let mediaSelection = 0;
  const imageCache = new Map();
  const preloadImage = (source) => {
    if (!source) return Promise.reject(new Error('missing_image'));
    if (!imageCache.has(source)) {
      const image = new Image();
      image.src = source;
      const ready = image.decode().then(() => image).catch(error => { imageCache.delete(source); throw error; });
      imageCache.set(source, ready);
      if (imageCache.size > 20) imageCache.delete(imageCache.keys().next().value);
    }
    return imageCache.get(source);
  };

  const rootUrl = () => window.Shopify?.routes?.root || '/';

  const close = ({ restoreFocus = true } = {}) => {
    mediaSelection++;
    priceController?.abort();
    requestController?.abort();
    requestController = null;
    const media = trigger?.closest('.product-card__media');
    if (media) {
      media.setAttribute('data-preview-reset', '');
      const reset = () => media.removeAttribute('data-preview-reset');
      media.addEventListener('pointerleave', reset, { once: true });
      media.addEventListener('pointerenter', reset, { once: true });
    }
    if (dialog.open) dialog.close();
    document.body.classList.remove('quick-view-open');
    if (restoreFocus && trigger?.isConnected) trigger.focus();
  };

  const showStatus = (message, busy = false) => {
    status.textContent = message;
    dialog.toggleAttribute('aria-busy', busy);
  };

  const open = async (button) => {
    const selectionStartedAt = Date.now();
    const productUrl = button.dataset.productUrl;
    if (!productUrl) return;

    priceController?.abort();
    requestController?.abort();
    const controller = new AbortController();
    requestController = controller;
    if (!dialog.open) trigger = button;
    const existingForm = content.querySelector('[data-quick-view-form]');
    if (existingForm) existingForm.inert = true;
    showStatus(dialog.dataset.loadingLabel, true);

    if (!dialog.open) dialog.showModal();
    document.body.classList.add('quick-view-open');

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('section_id', 'quick-view');
      const responseSource = window.NavelisProductResponses?.status(url.href) || 'network';
      let markup;
      if (window.NavelisProductResponses) {
        markup = await window.NavelisProductResponses.read(url.href, { signal: controller.signal, errorMessage: dialog.dataset.errorLabel });
      } else {
        const response = await fetch(url, { headers: { Accept: 'text/html' }, signal: controller.signal });
        if (!response.ok) throw new Error(dialog.dataset.errorLabel);
        markup = await response.text();
      }
      const html = new DOMParser().parseFromString(markup, 'text/html');
      if (controller.signal.aborted || requestController !== controller || !dialog.open) return;
      const product = html.querySelector('[data-quick-view-product]');
      if (!product) throw new Error(dialog.dataset.errorLabel);

      dialog.dataset.productUpdateMs = String(Date.now() - selectionStartedAt);
      dialog.dataset.productResponse = responseSource;
      content.replaceChildren(product);
      mediaSelection++;
      if (!navigator.connection?.saveData) {
        const warm = () => {
          if (!product.isConnected || !dialog.open) return;
          [...product.querySelectorAll('[data-quick-view-media]')].slice(0, 5).forEach(button => preloadImage(button.dataset.imageSrc).catch(() => {}));
        };
        if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 1000 });
        else setTimeout(warm, 300);
      }
      showStatus('', false);
      content.querySelector('select, input, button, a')?.focus();
    } catch (error) {
      if (controller.signal.aborted || requestController !== controller) return;
      if (existingForm) existingForm.inert = false;
      showStatus(error.name === 'AbortError' ? dialog.dataset.errorLabel : error.message || dialog.dataset.errorLabel, false);
    }
  };

  const updateMedia = async (source, alt = '') => {
    const image = content.querySelector('[data-quick-view-main-image]');
    if (!image || !source) return;
    const selection = ++mediaSelection;
    try { await preloadImage(source); } catch { return; }
    if (selection !== mediaSelection || !image.isConnected || !dialog.open) return;
    image.parentElement.querySelectorAll('[data-media-outgoing]').forEach(item => item.remove());
    image.getAnimations().forEach(animation => animation.cancel());
    const previous = image.cloneNode();
    previous.removeAttribute('data-quick-view-main-image');
    previous.setAttribute('data-media-outgoing', '');
    previous.setAttribute('aria-hidden', 'true');
    previous.alt = '';
    image.parentElement.append(previous);
    image.src = source;
    image.srcset = '';
    image.alt = alt;
    const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 520;
    image.animate([{ opacity: 0, transform: 'scale(1.018)' }, { opacity: 1, transform: 'scale(1)' }], { duration, easing: 'cubic-bezier(.22,1,.36,1)' });
    const fade = previous.animate([{ opacity: 1 }, { opacity: 0 }], { duration, easing: 'ease-out', fill: 'forwards' });
    fade.finished.then(() => previous.remove()).catch(() => previous.remove());
  };

  const updatePrice = async (option) => {
    priceController?.abort();
    const price = content.querySelector('[data-quick-view-price]');
    if (!price) return;
    if (option.dataset.volumePrice === 'true') {
      const controller = new AbortController();
      priceController = controller;
      price.replaceChildren();
      price.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(option.dataset.variantUrl, window.location.origin);
        url.searchParams.set('section_id', 'quick-view');
        const response = await fetch(url, { headers: { Accept: 'text/html' }, signal: controller.signal });
        if (!response.ok) throw new Error('price_unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = html.querySelector('[data-quick-view-price]');
        if (controller.signal.aborted || !price.isConnected) return;
        if (!fresh) throw new Error('price_unavailable');
        price.replaceChildren(...fresh.childNodes);
      } catch (error) {
        if (!controller.signal.aborted && error.name !== 'AbortError') showStatus(dialog.dataset.errorLabel, false);
      } finally { if (!controller.signal.aborted) price.removeAttribute('aria-busy'); }
      return;
    }
    price.removeAttribute('aria-busy');
    price.innerHTML = `<div class="price${option.dataset.comparePrice ? ' price--sale' : ''}"><span class="price__current"></span>${option.dataset.comparePrice ? '<s class="price__compare"></s>' : ''}</div>`;
    price.querySelector('.price__current').textContent = option.dataset.price;
    if (option.dataset.comparePrice) price.querySelector('.price__compare').textContent = option.dataset.comparePrice;
  };

  document.addEventListener('click', (event) => {
    const quickViewTrigger = event.target.closest('[data-quick-view-trigger]');
    if (quickViewTrigger) {
      if (quickViewTrigger.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)) return;
      event.preventDefault();
      open(quickViewTrigger);
      return;
    }

    if (event.target.closest('[data-quick-view-close]')) {
      close();
      return;
    }

    if (event.target === dialog) {
      close();
      return;
    }

    const mediaButton = event.target.closest('[data-quick-view-media]');
    if (!mediaButton) return;
    updateMedia(mediaButton.dataset.imageSrc, mediaButton.dataset.imageAlt);
    content.querySelectorAll('[data-quick-view-media]').forEach((button) => {
      const active = button === mediaButton;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-quick-view-variant]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const add = content.querySelector('[data-quick-view-add]');
    if (!option || !add) return;

    const available = option.dataset.available === 'true';
    add.disabled = !available;
    add.textContent = available ? (option.dataset.preorder === 'true' ? add.dataset.preorderLabel : add.dataset.addLabel) : add.dataset.soldOutLabel;
    const quantity = content.querySelector('[name="quantity"]');
    if (quantity && window.TemporiQuantityRules) {
      quantity.dataset.ruleMin = option.dataset.ruleMin;
      quantity.dataset.ruleIncrement = option.dataset.ruleIncrement;
      quantity.dataset.ruleMax = option.dataset.ruleMax;
      quantity.dataset.variantId = option.value;
      quantity.setAttribute('data-quantity-rule-input', '');
      const bounds = window.TemporiQuantityRules.apply(quantity, Number(option.dataset.cartQuantity));
      quantity.value = bounds.allowed ? bounds.minimum : 0;
      add.disabled = !available || !bounds.allowed;
    }
    updatePrice(option);
    updateMedia(option.dataset.imageSrc, option.dataset.imageAlt);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-quick-view-form]');
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('[data-quick-view-add]');
    const formStatus = form.querySelector('[data-quick-view-form-status]');
    if (!button || form.inert || dialog.hasAttribute('aria-busy') || button.disabled || button.dataset.loading === 'true') return;

    button.dataset.loading = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const quantity = form.querySelector('[name="quantity"]');
      if (quantity && window.TemporiQuantityRules) {
        const response = await fetch(rootUrl() + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error(button.dataset.quantityError);
        const cart = await response.json();
        const variantId = new FormData(form).get('id');
        const count = cart.items.filter(item => String(item.variant_id) === String(variantId)).reduce((sum, item) => sum + Number(item.quantity), 0);
        window.TemporiQuantityRules.apply(quantity, count);
        if (!window.TemporiQuantityRules.valid(Number(quantity.value), window.TemporiQuantityRules.ruleFor(quantity), count)) {
          quantity.focus();
          throw new Error(button.dataset.quantityError);
        }
      }
      if (window.NavelisCart) {
        close({ restoreFocus: false });
        await window.NavelisCart.add(new FormData(form), trigger);
      } else {
        const response = await fetch(`${rootUrl()}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error(button.dataset.errorLabel);
        close();
        window.location.assign(`${rootUrl()}cart`);
      }
    } catch (error) {
      if (!dialog.open) dialog.showModal();
      document.body.classList.add('quick-view-open');
      if (formStatus) formStatus.textContent = error.message || button.dataset.errorLabel;
      button.disabled = false;
    } finally {
      button.removeAttribute('aria-busy');
      delete button.dataset.loading;
    }
  });

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  dialog.addEventListener('close', () => document.body.classList.remove('quick-view-open'));
  window.NavelisQuickView = { close, open };
})();
